<template>
  <div v-if="profile && profile.fields.length" class="space-y-6">
    <div v-for="field in profile.fields" :key="field.key">
      <label :for="`industry-attr-${field.key}`" class="font-medium text-sm mb-2 block">
        {{ field.label }}
        <span v-if="field.type === 'multiselect'" class="text-muted-foreground text-xs font-normal">
          (select all that apply)
        </span>
      </label>

      <div
        v-if="field.type === 'multiselect'"
        :id="`industry-attr-${field.key}`"
        class="flex flex-wrap gap-2"
        role="group"
        :aria-invalid="!!errors?.[field.key]"
      >
        <button
          v-for="option in field.options"
          :key="option.value"
          type="button"
          :aria-pressed="selectedValues(field.key).includes(option.value)"
          @click="toggle(field.key, option.value)"
          class="px-3 py-1 rounded-full border text-sm transition-colors"
          :class="selectedValues(field.key).includes(option.value)
            ? 'bg-brand text-white border-brand'
            : 'border-muted-foreground/30 text-muted-foreground hover:border-brand hover:text-foreground'"
        >
          {{ option.label }}
        </button>
      </div>

      <select
        v-else
        :id="`industry-attr-${field.key}`"
        :value="selectedValues(field.key)[0] ?? ''"
        @change="setSingle(field.key, ($event.target as HTMLSelectElement).value)"
        class="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
        :aria-invalid="!!errors?.[field.key]"
      >
        <option value="">Select {{ field.label.toLowerCase() }}</option>
        <option v-for="option in field.options" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>

      <p v-if="errors?.[field.key]" class="text-sm text-red-600 mt-1">{{ errors[field.key] }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { CompanyIndustryAttributes, CompanyIndustryProfile } from '@/types/company'

const props = defineProps<{
  profile: CompanyIndustryProfile | null
  modelValue: CompanyIndustryAttributes | null | undefined
  errors?: Record<string, string>
}>()

const emits = defineEmits<{
  (e: 'update:modelValue', value: CompanyIndustryAttributes): void
}>()

const selectedValues = (key: string): string[] => {
  const value = props.modelValue?.[key]
  if (Array.isArray(value)) return value
  return typeof value === 'string' && value !== '' ? [value] : []
}

const emitWith = (key: string, value: string | string[] | null) => {
  const next: CompanyIndustryAttributes = { ...(props.modelValue ?? {}) }
  if (value === null || (Array.isArray(value) && value.length === 0)) {
    delete next[key]
  } else {
    next[key] = value
  }
  emits('update:modelValue', next)
}

const toggle = (key: string, value: string) => {
  const current = selectedValues(key)
  emitWith(
    key,
    current.includes(value) ? current.filter((item) => item !== value) : [...current, value]
  )
}

const setSingle = (key: string, value: string) => emitWith(key, value || null)
</script>
